import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Send, Lock, Globe } from 'lucide-react'
import { adminApi, type SupportTicket } from '../services/api'
import { StatusBadge } from '../components/StatusBadge'
import { PriorityBadge } from '../components/PriorityBadge'
import { CategoryIcon } from '../components/CategoryIcon'

const STATUS_TRANSITIONS: Record<string, string[]> = {
  open: ['awaiting_response', 'in_progress', 'resolved', 'closed'],
  awaiting_response: ['in_progress', 'resolved', 'closed'],
  in_progress: ['awaiting_response', 'resolved', 'closed'],
  resolved: ['closed', 'open'],
  closed: ['open'],
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function AdminTicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const [ticket, setTicket] = useState<SupportTicket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reply, setReply] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [sending, setSending] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    if (!ticketId) return
    adminApi.getTicket(ticketId)
      .then((t) => { setTicket(t); setLoading(false) })
      .catch((e) => { setError(e.message); setLoading(false) })
  }, [ticketId])

  const handleReply = async () => {
    if (!reply.trim() || !ticketId) return
    setSending(true)
    try {
      const response = await adminApi.addResponse(ticketId, reply.trim(), isInternal)
      setReply('')
      setTicket((prev) =>
        prev ? { ...prev, responses: [...(prev.responses ?? []), response] } : prev
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!ticketId) return
    setUpdatingStatus(true)
    try {
      const updated = await adminApi.updateStatus(ticketId, newStatus)
      setTicket((prev) => prev ? { ...prev, status: updated.status } : prev)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error && !ticket) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={() => navigate('/admin')} className="mt-4 text-sm text-brand-500">
          Back to queue
        </button>
      </div>
    )
  }

  if (!ticket) return null

  const availableTransitions = STATUS_TRANSITIONS[ticket.status] ?? []

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back nav */}
      <button
        onClick={() => navigate('/admin')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to queue
      </button>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
              <CategoryIcon category={ticket.category} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 leading-snug">{ticket.subject}</h1>
            <p className="text-xs text-gray-500 mt-1">
              From <span className="font-medium">{(ticket as unknown as Record<string, string>).user_email ?? ticket.user_id}</span>
              {ticket.assignee_name && (
                <> · Assigned to <span className="font-medium">{ticket.assignee_name}</span></>
              )}
              {' · '}Opened {fmtTime(ticket.created_at)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
        </div>

        {/* Description */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
          {ticket.description}
        </div>

        {/* Status transitions */}
        {availableTransitions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 self-center">Move to:</span>
            {availableTransitions.map((s) => (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                disabled={updatingStatus}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 hover:border-brand-400 hover:text-brand-600 transition-colors disabled:opacity-40"
              >
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Thread */}
      <div className="space-y-3 mb-4">
        {(ticket.responses ?? []).map((r) => {
          const isAI = r.responder_type === 'ai'
          const isInternalNote = r.is_internal
          return (
            <div
              key={r.id}
              className={`rounded-xl border p-4 text-sm leading-relaxed ${
                isInternalNote
                  ? 'bg-amber-50 border-amber-200'
                  : isAI
                  ? 'bg-blue-50 border-blue-200'
                  : r.responder_type === 'human'
                  ? 'bg-white border-gray-200 ml-8'
                  : 'bg-gray-50 border-gray-100'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5 text-xs text-gray-500">
                {isInternalNote && <Lock className="w-3 h-3 text-amber-500" />}
                <span className="font-medium text-gray-700">
                  {isInternalNote
                    ? 'Internal note'
                    : isAI
                    ? 'Llamigo AI'
                    : r.responder_name ?? 'Support team'}
                </span>
                <span>·</span>
                <span>{fmtTime(r.created_at)}</span>
                {isAI && r.ai_confidence != null && (
                  <span className="text-blue-400">({Math.round(r.ai_confidence * 100)}% confidence)</span>
                )}
              </div>
              <p className="text-gray-800 whitespace-pre-wrap">{r.content}</p>
            </div>
          )
        })}
      </div>

      {/* Reply box */}
      {ticket.status !== 'closed' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {/* Internal toggle */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setIsInternal(false)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                !isInternal
                  ? 'bg-brand-500 text-white'
                  : 'text-gray-500 hover:text-gray-700 border border-gray-200'
              }`}
            >
              <Globe className="w-3 h-3" />
              Reply to customer
            </button>
            <button
              onClick={() => setIsInternal(true)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                isInternal
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-500 hover:text-gray-700 border border-gray-200'
              }`}
            >
              <Lock className="w-3 h-3" />
              Internal note
            </button>
          </div>

          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={isInternal ? 'Internal note visible only to staff…' : 'Reply to the customer…'}
            rows={4}
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 resize-y ${
              isInternal
                ? 'border-amber-200 focus:ring-amber-400 bg-amber-50'
                : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500'
            }`}
          />

          {error && (
            <p className="text-xs text-red-500 mt-1">{error}</p>
          )}

          <div className="flex justify-end mt-3">
            <button
              onClick={handleReply}
              disabled={sending || !reply.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isInternal ? 'Save note' : 'Send reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
